/**
 * /payroll/year-end/w3/:year — W-3 Transmittal PDF.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Alert, Box, Button, Card, CardContent, IconButton, Stack, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/year-end_/w3/$year")({
  component: W3Page,
});

function W3Page() {
  const { year } = Route.useParams();
  const yr = Number(year);

  const downloadPdf = () => {
    const url = `/api/payroll/year-end/w3/${yr}.pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `Form-W3-${yr}.pdf`; a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton component={Link} to="/payroll/year-end" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">Back to Year-End</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Form W-3 — {yr}</Typography>
          <Typography variant="body2" color="text.secondary">
            Transmittal of Wage and Tax Statements (summary of W-2 totals).
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HelpHint docPath="payroll-year-end/filing-w2.md" label="W-3 filing help" />
          </Box>
        </Box>
        <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={downloadPdf}>
          Download W-3 PDF
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Most filers don't need W-3.</strong> When you transmit W-2s via{" "}
            <Link to={`/payroll/year-end/w2/${yr}`}>IRIS / SSA BSO e-file</Link>, the W-3 transmittal
            is generated automatically server-side. Use this paper W-3 only if you're filing W-2s by
            mail.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The PDF totals are computed by re-aggregating every W-2-eligible paycheck for {yr}.
            Make sure all pay runs for the year are <strong>posted</strong> before downloading.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
